// The Stage in the zone framework (docs/build/zones.md): main.ts imports this module for its side effects. It
// registers the zone's own section kinds (zones/events/zone.json "sections": next-up, programme, venues, host) and
// its Home portal for Haunt night. Each section draws at once from the zone's dated moments, then redraws if
// rooms' calendar answers with more, so the first screen never waits on the network and is never empty.
//
// Section kinds are global, so each handler draws only for this zone and hands any other zone to whatever was
// registered before it.

import { h, link } from "../../dom.ts";
import { ApiError } from "../../api.ts";
import { dayKey, eventError, KIND_LABEL, loadCalendar, setRsvp, viewerZone, type PlazaEvent } from "../../events.ts";
import { hostPanel, joinButton } from "../../pages/events.ts";
import { onPhone } from "../../plaza.ts";
import { rerender } from "../../router.ts";
import { currentMe, signedIn } from "../../session.ts";
import { reducedMotion, zoneVars } from "../../tokens.ts";
import { registerPortal, registerSection, sectionRenderer, zoneById, zoneHref, type SectionContext, type SectionRender } from "../registry.ts";
import { mediaUrl, type Zone } from "../spec.ts";
import { downloadIcs, goingIds, needsRsvpSync, setGoing, toggleGoing } from "./ics.ts";
import { offerAsk, withdrawAsk } from "../../notify-ask.ts";
import { artVariants, bands, clockLine, joinsNow, countdownText, dateZone, itemWhen, momentItems, nextUp, programme, timeColumn, VENUES, type Item, type ItemKind } from "./programme.ts";
import { ensureStageStyle } from "./style.ts";

export const ZONE_ID = "events";

// ---- rooms' calendar, once per page view (30 s), never an error on the page ----

let cal: { at: number; p: Promise<PlazaEvent[]> } | null = null;
function roomsEvents(): Promise<PlazaEvent[]> {
  if (!cal || Date.now() - cal.at > 30_000) cal = { at: Date.now(), p: loadCalendar().then((c) => c.events, () => []) };
  return cal.p;
}

/** Calls `f` every second while `el` is on the page (it waits for the first insert). */
function tick(el: HTMLElement, f: (now: number) => void): void {
  let seen = false;
  const id = window.setInterval(() => {
    if (!el.isConnected) {
      if (seen) window.clearInterval(id);
      return;
    }
    seen = true;
    f(Date.now());
  }, 1000);
}

/** Register `kind` for this zone only, keeping any earlier renderer for other zones. */
function own(kind: string, render: SectionRender): void {
  const before = sectionRenderer(kind);
  registerSection(kind, (ctx) => {
    if (ctx.zone.id !== ZONE_ID) return before ? before(ctx) : null;
    ensureStageStyle();
    return render(ctx);
  });
}

/** Draw from the zone's moments now; redraw in place once rooms adds events. */
function withRooms(ctx: SectionContext, draw: (items: Item[], now: number) => HTMLElement | null): HTMLElement | null | Promise<HTMLElement | null> {
  const first = draw(programme(ctx.zone.moments, [], ctx.now), ctx.now);
  const later = roomsEvents().then((evs) => (evs.length ? draw(programme(ctx.zone.moments, evs, Date.now()), Date.now()) : undefined));
  if (!first) return later.then((el) => el ?? null);
  void later.then((el) => {
    if (el === undefined || !first.parentNode) return;
    if (el) first.replaceWith(el);
    else first.closest(".zone-section")?.remove();
  });
  return first;
}

const KIND_TAG: Record<ItemKind, string> = { night: "Plaza night", season: "Season", deadline: "Deadline", ...KIND_LABEL };
const cap = (s: string) => s[0]!.toUpperCase() + s.slice(1);
const canRsvp = (i: Item) => i.kind !== "season" && i.kind !== "deadline";

/** Join, into the event's instance; a phone can't run the app, so it says where to join instead of offering a download. */
function joinEl(i: Item): HTMLElement {
  return onPhone() ? h("p", { class: "st-join-note" }, "On now in the Plaza. Join from the Banger app on your computer.") : joinButton(i.instance ?? "", "Join");
}

/** "Visit the Plaza" with nothing booked; on a phone, where to join instead (no app to open there). */
function visitEl(): HTMLElement {
  return onPhone() ? h("p", { class: "st-join-note" }, "The Plaza is open any time. Join from the Banger app on your computer.") : joinButton("1", "Visit the Plaza");
}

/** The art as CSS variables: `--art` (wide) and `--art-phone` (style.ts swaps it in at phone width). */
const artVars = (art: string) => `--art: url("${art}"); --art-phone: url("${artVariants(art).phone}")`;

// ---- shared bits: the clock, RSVP and add-to-calendar ----

function clock(i: Item, now: number, cls = "st-clock"): { el: HTMLElement; update: (t: number) => void } {
  const label = h("span", { class: "st-clock-label" });
  const digits = h("span", { class: "st-clock-digits" });
  const el = h("p", { class: cls, role: "timer", "aria-live": "off" }, label, digits);
  const update = (t: number) => {
    const c = clockLine(i, t);
    label.textContent = c.label;
    digits.textContent = c.target ? countdownText(c.target, t) : "";
    el.classList.toggle("st-live", c.live);
  };
  update(now);
  return { el, update };
}

/** Rooms event ids whose device mark was sent to rooms this page view (needsRsvpSync). */
const rsvpSent = new Set<string>();

const REMIND = "You're on the list. We'll remind you in your Banger inbox when it starts.";
/** w13-notify: while "Remind you 15 minutes before?" is asked, the note only says this */
const ON_LIST = "You're on the list.";

/**
 * "I'm going". Signed in, on a night rooms holds: the RSVP goes to rooms, which reminds you in your inbox when it
 * starts. Signed out, on a fixed date rooms doesn't hold yet, or with rooms unreachable: kept on this device, and
 * sent to rooms once you're signed in (the next time the button is drawn), so the reminder really happens.
 */
function rsvpButton(i: Item, note: HTMLElement | null, small = false): HTMLElement {
  const show = (on: boolean) => {
    b.setAttribute("aria-pressed", String(on));
    b.textContent = on ? "You're going" : "I'm going";
  };
  const say = (t: string) => {
    if (note) note.textContent = t;
  };
  const b = h("button", {
    type: "button",
    class: `st-btn${small ? " small" : " primary"}`,
    "aria-label": `RSVP: ${i.title}`,
    onclick: async () => {
      const want = b.getAttribute("aria-pressed") !== "true";
      if (i.eventId && signedIn()) {
        b.disabled = true;
        try {
          const r = await setRsvp(i.eventId, want);
          setGoing(i.id, r.going);
          show(r.going);
          say(r.going ? REMIND : "");
          // wave 13 (w13-notify): the moment a reminder is worth asking for. While it's asked, the note doesn't
          // promise a reminder of its own; it sits above the ask.
          const scope = b.closest(".st-next-copy, .st-item");
          if (!r.going) withdrawAsk(scope, "eventReminder");
          else {
            const visibleNote = note && !note.classList.contains("st-sr") && note.isConnected ? note : null;
            if (await offerAsk(visibleNote ?? b.closest(".st-actions, .st-item-actions") ?? b, "eventReminder")) say(ON_LIST);
          }
          return;
        } catch (e) {
          const unreachable = !(e instanceof ApiError) || e.status >= 500 || e.code === "service_unavailable";
          if (!unreachable) return say(eventError(e));
        } finally {
          b.disabled = false;
        }
      }
      const on = toggleGoing(i.id);
      show(on);
      // signed out: the mark is sent to rooms once they sign in (below); rooms unreachable or a fixed date: only the
      // calendar file reminds them, so say so
      say(!on ? "" : i.eventId && !signedIn() ? "Saved on this device. Sign in and we'll put you on the list, with a reminder in your Banger inbox when it starts." : "Saved on this device. Add it to your calendar for a reminder.");
    },
  });
  show(goingIds().has(i.id));
  if (needsRsvpSync(i, signedIn(), goingIds(), rsvpSent)) {
    rsvpSent.add(i.eventId!);
    void setRsvp(i.eventId!, true).then(
      (r) => {
        setGoing(i.id, r.going);
        show(r.going);
        if (r.going) say(REMIND);
      },
      () => rsvpSent.delete(i.eventId!), // try again on the next draw
    );
  }
  return b;
}

function icsButton(i: Item, small = false): HTMLElement {
  return h(
    "button",
    { type: "button", class: `st-btn${small ? " small" : ""}`, "aria-label": `Add ${i.title} to your calendar`, onclick: () => downloadIcs(i) },
    "Add to calendar",
  );
}

function venueLine(i: Item): (Node | string)[] {
  if (i.venue) return ["On ", h("strong", null, VENUES[i.venue].name), ", in the Plaza"];
  return i.kind === "season" ? ["Everywhere on Banger"] : ["Online, on Banger"];
}

// ---- next up: one big card for the next night ----

function nextUpEl(items: Item[], now: number, tz: string): HTMLElement {
  const it = nextUp(items, now);
  if (!it) return quiet();
  const note = h("p", { class: "st-note", "aria-live": "polite" });
  const c = clock(it, now);
  const kicker = it.live ? "Live now" : dayKey(it.at, dateZone(it, tz)) === dayKey(now, tz) ? "Tonight" : "Next up";
  // "Going" becomes "Join" once the night is on (wave 13): straight into the world, in its instance when rooms holds it
  const join = joinsNow(it);
  const about = it.href.startsWith("/") ? link(it.href, { class: "st-btn" }, it.kind === "season" ? "Enter Open Haunt" : "About the night") : null;
  const more = join ? [joinEl(it), about] : about;
  const el = h(
    "section",
    // the zone's hero while there's a next night (the brief: "the next event's art, full bleed"); style.ts hides the
    // spotlight poster then, so the title here is the page's h1 and "The Stage" its eyebrow
    { class: `st st-next st-hero${it.live ? " st-live" : ""}`, id: "next-up", "aria-label": `${kicker}: ${it.title}` },
    h("div", { class: "st-next-art", style: artVars(it.art), role: "img", "aria-label": `Art for ${it.title}` }),
    h(
      "div",
      { class: "st-next-copy" },
      h("p", { class: "st-kicker" }, h("span", { class: "st-dot", "aria-hidden": "true" }), h("span", { class: "st-zone-name" }, "The Stage"), kicker),
      h("h1", { class: "st-display" }, it.title),
      h("p", { class: "st-next-venue st-dim" }, ...venueLine(it)),
      h("p", { class: "st-next-when" }, itemWhen(it, tz)),
      c.el,
      h("p", { class: "st-next-line" }, it.line),
      h("div", { class: "st-actions" }, canRsvp(it) && !join ? rsvpButton(it, note) : null, it.kind === "season" || join ? null : icsButton(it), more),
      canRsvp(it) && !join ? note : null,
    ),
  );
  tick(el, c.update);
  return el;
}

/** Nothing ahead: the stage poster, and the Plaza, which is always open. */
function quiet(): HTMLElement {
  return h(
    "section",
    { class: "st st-next quiet", id: "next-up", "aria-label": "This week on stage" },
    h("div", { class: "st-next-art", style: artVars(mediaUrl("zones/events/plaza.jpg")!), "aria-hidden": "true" }),
    h(
      "div",
      { class: "st-next-copy" },
      h("p", { class: "st-kicker" }, h("span", { class: "st-dot", "aria-hidden": "true" }), "This week on stage"),
      h("h2", { class: "st-display" }, "The Plaza is always open"),
      h("p", { class: "st-next-line" }, "The next night is being booked. Until then, drop in, meet people and jump into games through the portals."),
      h("div", { class: "st-actions" }, visitEl()),
    ),
  );
}

// ---- the programme: tonight / this week / this season ----

function programmeEl(items: Item[], now: number, tz: string): HTMLElement {
  const updates: ((t: number) => void)[] = [];
  const list = bands(items, now, tz);
  const el = h(
    "section",
    { class: "st st-prog", id: "programme", "aria-label": "The programme" },
    h("div", { class: "st-prog-head" }, h("h2", { class: "st-display" }, "The programme"), h("p", { class: "st-dim", style: { margin: "0" } }, `Times in your time zone, ${tz.replace(/_/g, " ")}.`)),
    list.length
      ? list.map((b) =>
          h(
            "section",
            { class: "st-band", "data-band": b.id, "aria-label": b.title },
            h("h3", { class: "st-band-h" }, b.title, h("span", null, b.items.length === 1 ? "1 on the bill" : `${b.items.length} on the bill`)),
            h("ol", { class: "st-list", role: "list" }, b.items.map((i) => programmeItem(i, now, tz, updates))),
          ),
        )
      : h(
          "div",
          { class: "st-open" },
          h("div", null, h("h3", null, "The Plaza is always open"), h("p", null, "Nothing is booked on stage yet. Drop in, meet people and jump into games through the portals.")),
          visitEl(),
        ),
  );
  if (updates.length) tick(el, (t) => updates.forEach((f) => f(t)));
  return el;
}

function programmeItem(i: Item, now: number, tz: string, updates: ((t: number) => void)[]): HTMLElement {
  const t = timeColumn(i, tz);
  const soon = i.live || (i.at > now && i.at - now < 2 * 86_400_000) || (i.kind === "season" && i.at > now);
  let clk: HTMLElement | null = null;
  if (soon) {
    const c = clock(i, now, "st-clock st-item-clock");
    updates.push(c.update);
    clk = c.el;
  }
  const art = h("span", { class: "st-card-art", style: { backgroundImage: `url("${artVariants(i.art).card}")` }, role: "img", "aria-label": `Art for ${i.title}` });
  const copy = h(
    "span",
    { class: "st-card-copy" },
    h("span", { class: "st-tag" }, h("em", null, KIND_TAG[i.kind]), i.venue ? ` · ${VENUES[i.venue].name}` : null, i.rsvps ? ` · ${i.rsvps} going` : null),
    h("strong", null, i.title),
    h("span", { class: "st-line" }, i.line),
    clk,
  );
  const card = i.href.startsWith("/") ? link(i.href, { class: "st-card" }, art, copy) : h("div", { class: "st-card" }, art, copy);
  const note = h("p", { class: "st-note st-sr", "aria-live": "polite" });
  const join = joinsNow(i);
  const actions = (join ? [joinEl(i)] : [canRsvp(i) ? rsvpButton(i, note, true) : null, i.kind === "season" ? null : icsButton(i, true)]).filter((x): x is HTMLElement => !!x);
  return h(
    "li",
    { class: `st-item${i.live ? " st-live" : ""}`, id: `on-${i.id}`, "data-kind": i.kind },
    h("div", { class: "st-time" }, h("b", null, t.big), h("span", null, t.small)),
    card,
    actions.length ? h("div", { class: "st-item-actions" }, actions, note) : null,
  );
}

// ---- venues ----

function venuesEl(items: Item[], now: number, tz: string): HTMLElement {
  return h(
    "section",
    { class: "st st-venues", id: "venues", "aria-label": "Venues in the Plaza" },
    h("h2", { class: "st-display" }, "Venues in the Plaza"),
    h(
      "ul",
      { class: "st-venue-grid", role: "list" },
      Object.values(VENUES).map((v) => {
        const next = items.find((i) => i.venue === v.id && i.until > now);
        return h(
          "li",
          { class: "st-venue", "data-venue": v.id },
          h("img", { class: "st-venue-art", src: artVariants(v.art).card, alt: "", width: 960, height: 600, loading: "lazy", decoding: "async" }),
          h(
            "div",
            { class: "st-venue-copy" },
            h("span", { class: "st-tag" }, h("em", null, v.what)),
            h("h3", null, cap(v.name)),
            h("p", null, v.line),
            next ? h("p", { class: "st-next-here" }, h("b", null, next.live ? "Live now: " : "Next here: "), next.title, next.live ? null : `, ${itemWhen(next, tz)}`) : null,
          ),
        );
      }),
    ),
  );
}

own("next-up", (ctx) => withRooms(ctx, (items, now) => nextUpEl(items, now, viewerZone())));
own("programme", (ctx) => withRooms(ctx, (items, now) => programmeEl(items, now, viewerZone())));
own("venues", (ctx) => withRooms(ctx, (items, now) => venuesEl(items, now, viewerZone())));
own("host", () =>
  h(
    "div",
    { class: "st st-host", id: "host" },
    hostPanel(currentMe(), async () => {
      cal = null;
      rerender();
    }),
  ),
);

// ---- the night's Home portal: Haunt night in Open Haunt's colours, with its countdown ----

/** The portal banner while zone.json's homePortal is up (5 Nov – 8 Nov) and the night is ahead or live; else null (the default). */
export function nightPortal(z: Zone, now: number): HTMLElement | null {
  const p = z.homePortal;
  const night = momentItems(z.moments, now).find((i) => i.kind === "night");
  if (!p || !night) return null;
  ensureStageStyle();
  const haunt = zoneById("open-haunt");
  const c = clock(night, now);
  const el = link(
    `${zoneHref(z)}#next-up`,
    { class: "st-portal", "aria-label": `${p.title ?? night.title}: ${p.copy}` },
    h("span", { class: "st-portal-art", style: { backgroundImage: `url("${night.art}")` }, "aria-hidden": "true" }),
    h(
      "span",
      { class: "st-portal-copy" },
      p.eyebrow ? h("span", { class: "st-kicker" }, h("span", { class: "st-dot", "aria-hidden": "true" }), p.eyebrow) : null,
      h("strong", { class: "st-portal-title" }, p.title ?? night.title),
      h("span", null, p.copy),
      c.el,
      h("span", { class: "st-portal-cta" }, p.cta ?? "See the night"),
    ),
  );
  for (const [k, v] of Object.entries(zoneVars(haunt ?? z, reducedMotion()))) el.style.setProperty(k, v);
  tick(el, c.update);
  return el;
}

registerPortal(ZONE_ID, (z, now) => nightPortal(z, now));
